/**
 * Destination-focused data operations: backfill, translations, images, hierarchy.
 */
import React, { useCallback, useState } from "react";
import { useSnackbarController } from "@firecms/core";
import { Button, Paper, Typography, TextField } from "@firecms/ui";
import { fetchWithAuth } from "./fetchWithAuth";
import { BatchDelayField } from "./BatchDelayField";
import { BatchResultBar } from "./BatchResultBar";
import type { AdminDivisionListItem, BatchItemResult, DocListItem, DuplicateGroup } from "./types";

const DESTINATION_LEVELS = [
  { value: "", label: "Any level" },
  { value: "continent", label: "Continent" },
  { value: "country", label: "Country" },
  { value: "administrative_division", label: "Administrative division" },
  { value: "city", label: "City" },
  { value: "neighborhood", label: "Neighborhood" },
] as const;

async function runServerBatch(
  baseUrl: string,
  token: string,
  operation: string,
  document_ids: string[],
  delayMs: number,
  opts?: {
    force?: boolean;
    fill_empty?: boolean;
    refetch_images?: boolean;
    mode?: "replace" | "append";
    translate_mode?: "force" | "fill_empty" | "stale_only";
  }
): Promise<{ ok: boolean; results: BatchItemResult[]; error?: string }> {
  const res = await fetchWithAuth(`${baseUrl}/api/batch/run`, token, {
    operation,
    document_ids,
    delay_ms: delayMs,
    force: opts?.force ?? false,
    fill_empty: opts?.fill_empty ?? false,
    refetch_images: opts?.refetch_images ?? false,
    ...(opts?.mode ? { mode: opts.mode } : {}),
    ...(opts?.translate_mode ? { translate_mode: opts.translate_mode } : {}),
  });
  const data = (await res.json()) as {
    results?: Array<{ id: string; ok: boolean; error?: string }>;
    error?: string;
  };
  if (!res.ok) {
    return { ok: false, results: [], error: data.error ?? "Batch failed" };
  }
  const results = (data.results ?? []).map((r) => ({
    id: r.id,
    ok: r.ok,
    error: r.error,
  }));
  return { ok: true, results };
}

export interface DestinationDataToolsProps {
  baseUrl: string;
  getToken: () => Promise<string | null>;
  batchDelaySec: number;
}

export function DestinationDataTools({
  baseUrl,
  getToken,
  batchDelaySec,
}: DestinationDataToolsProps) {
  const snackbar = useSnackbarController();
  const delayMs = batchDelaySec * 1000;

  const [incomplete, setIncomplete] = useState<DocListItem[] | null>(null);
  const [partialContent, setPartialContent] = useState<DocListItem[] | null>(null);
  const [missingImages, setMissingImages] = useState<DocListItem[] | null>(null);
  const [withImages, setWithImages] = useState<Array<DocListItem & { image_count?: number }> | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateGroup[][] | null>(null);
  const [adminDivisions, setAdminDivisions] = useState<AdminDivisionListItem[] | null>(null);
  const [withContent, setWithContent] = useState<DocListItem[] | null>(null);
  const [loadingIncomplete, setLoadingIncomplete] = useState(false);
  const [loadingPartialContent, setLoadingPartialContent] = useState(false);
  const [loadingMissingImages, setLoadingMissingImages] = useState(false);
  const [loadingWithImages, setLoadingWithImages] = useState(false);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [loadingAdminDivisions, setLoadingAdminDivisions] = useState(false);
  const [loadingWithContent, setLoadingWithContent] = useState(false);
  const [backfilling, setBackfilling] = useState<string | "all" | null>(null);
  const [fillingEmpty, setFillingEmpty] = useState<string | "all" | null>(null);
  const [refetchingImages, setRefetchingImages] = useState<string | "all" | null>(null);
  const [appendingImages, setAppendingImages] = useState<string | "all" | null>(null);
  const [regeneratingContent, setRegeneratingContent] = useState<string | "all" | null>(null);
  const [regeneratingTranslations, setRegeneratingTranslations] = useState<string | "all" | null>(null);

  const [backfillBatchResults, setBackfillBatchResults] = useState<BatchItemResult[] | null>(null);
  const [partialBatchResults, setPartialBatchResults] = useState<BatchItemResult[] | null>(null);
  const [imageBatchResults, setImageBatchResults] = useState<BatchItemResult[] | null>(null);
  const [appendImageBatchResults, setAppendImageBatchResults] = useState<BatchItemResult[] | null>(null);
  const [adminBatchResults, setAdminBatchResults] = useState<BatchItemResult[] | null>(null);
  const [translateBatchResults, setTranslateBatchResults] = useState<BatchItemResult[] | null>(null);

  const [countryCode, setCountryCode] = useState("");
  const [populationThreshold, setPopulationThreshold] = useState(100_000);
  const [parentId, setParentId] = useState("");
  const [fixingParent, setFixingParent] = useState(false);
  const [fixingHierarchy, setFixingHierarchy] = useState(false);
  const [cleaningMidtiers, setCleaningMidtiers] = useState(false);
  const [dedupingTypes, setDedupingTypes] = useState(false);
  const [backfillingTier, setBackfillingTier] = useState(false);

  const [listFilterCountry, setListFilterCountry] = useState("");
  const [listFilterLevel, setListFilterLevel] = useState("");

  const listFilterPayload = useCallback((): Record<string, string> | undefined => {
    const o: Record<string, string> = {};
    const cc = listFilterCountry.trim().toUpperCase();
    if (/^[A-Z]{2}$/i.test(cc)) o.country_code = cc.slice(0, 2).toUpperCase();
    if (listFilterLevel) o.level = listFilterLevel;
    return Object.keys(o).length ? o : undefined;
  }, [listFilterCountry, listFilterLevel]);

  const loadIncomplete = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingIncomplete(true);
    setBackfillBatchResults(null);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/destinations/incomplete`, token, listFilterPayload() ?? {});
      const data = (await res.json()) as { destinations?: DocListItem[]; error?: string };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed to fetch" });
        return;
      }
      setIncomplete(data.destinations ?? []);
      snackbar.open({
        type: "success",
        message: `Found ${(data.destinations ?? []).length} destinations without content`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setLoadingIncomplete(false);
    }
  }, [baseUrl, getToken, snackbar, listFilterPayload]);

  const loadPartialContent = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingPartialContent(true);
    setPartialBatchResults(null);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/destinations/partial-content`, token, listFilterPayload() ?? {});
      const data = (await res.json()) as { destinations?: DocListItem[]; error?: string };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed to fetch" });
        return;
      }
      setPartialContent(data.destinations ?? []);
      snackbar.open({
        type: "success",
        message: `Found ${(data.destinations ?? []).length} destinations with empty content fields`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setLoadingPartialContent(false);
    }
  }, [baseUrl, getToken, snackbar, listFilterPayload]);

  const loadMissingImages = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingMissingImages(true);
    setImageBatchResults(null);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/destinations/missing-images`, token, listFilterPayload() ?? {});
      const data = (await res.json()) as { destinations?: DocListItem[]; error?: string };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed to fetch" });
        return;
      }
      setMissingImages(data.destinations ?? []);
      snackbar.open({
        type: "success",
        message: `Found ${(data.destinations ?? []).length} destinations with keywords but no images`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setLoadingMissingImages(false);
    }
  }, [baseUrl, getToken, snackbar, listFilterPayload]);

  const loadWithImages = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingWithImages(true);
    setAppendImageBatchResults(null);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/destinations/with-images`, token, listFilterPayload() ?? {});
      const data = (await res.json()) as {
        destinations?: Array<DocListItem & { image_count?: number }>;
        error?: string;
      };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed to fetch" });
        return;
      }
      setWithImages(data.destinations ?? []);
      snackbar.open({
        type: "success",
        message: `Found ${(data.destinations ?? []).length} destinations with images`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setLoadingWithImages(false);
    }
  }, [baseUrl, getToken, snackbar, listFilterPayload]);

  const loadDuplicates = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingDuplicates(true);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/destinations/duplicates`, token, listFilterPayload() ?? {});
      const data = (await res.json()) as { duplicates?: DuplicateGroup[][]; error?: string };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed to fetch" });
        return;
      }
      setDuplicates(data.duplicates ?? []);
      snackbar.open({
        type: "success",
        message: `Found ${(data.duplicates ?? []).length} duplicate groups`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setLoadingDuplicates(false);
    }
  }, [baseUrl, getToken, snackbar, listFilterPayload]);

  const runReFetchImages = useCallback(
    async (docId: string) => {
      const token = await getToken();
      if (!token) return;
      setRefetchingImages(docId);
      try {
        const res = await fetchWithAuth(`${baseUrl}/re-fetch-images`, token, {
          document_id: docId,
          mode: "replace",
        });
        const data = (await res.json()) as { ok?: boolean; message?: string; error?: string };
        if (!res.ok) {
          snackbar.open({ type: "error", message: data.error ?? "Failed" });
          return;
        }
        snackbar.open({ type: "success", message: data.message ?? "Images re-fetched" });
        setMissingImages((prev) => prev?.filter((d) => d._id !== docId) ?? null);
      } catch (err) {
        snackbar.open({
          type: "error",
          message: err instanceof Error ? err.message : "Request failed",
        });
      } finally {
        setRefetchingImages((p) => (p === docId ? null : p));
      }
    },
    [baseUrl, getToken, snackbar]
  );

  const runAppendImages = useCallback(
    async (docId: string) => {
      const token = await getToken();
      if (!token) return;
      setAppendingImages(docId);
      try {
        const res = await fetchWithAuth(`${baseUrl}/re-fetch-images`, token, {
          document_id: docId,
          mode: "append",
        });
        const data = (await res.json()) as { ok?: boolean; message?: string; error?: string };
        if (!res.ok) {
          snackbar.open({ type: "error", message: data.error ?? "Failed" });
          return;
        }
        snackbar.open({ type: "success", message: data.message ?? "Additional images fetched" });
      } catch (err) {
        snackbar.open({
          type: "error",
          message: err instanceof Error ? err.message : "Request failed",
        });
      } finally {
        setAppendingImages((p) => (p === docId ? null : p));
      }
    },
    [baseUrl, getToken, snackbar]
  );

  const runAllReFetchImages = useCallback(async () => {
    if (!missingImages?.length) return;
    setRefetchingImages("all");
    setImageBatchResults(null);
    const token = await getToken();
    if (!token) {
      setRefetchingImages(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "re_fetch_images",
      missingImages.map((d) => d._id),
      delayMs,
      { mode: "replace" }
    );
    setRefetchingImages(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setImageBatchResults(results);
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    setMissingImages((prev) => prev?.filter((d) => failedIds.has(d._id)) ?? null);
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Re-fetched images: ${successCount}/${results.length} succeeded`,
    });
  }, [missingImages, baseUrl, getToken, snackbar, delayMs]);

  const retryFailedImages = useCallback(async () => {
    if (!imageBatchResults?.length) return;
    const ids = imageBatchResults.filter((r) => !r.ok).map((r) => r.id);
    if (!ids.length) return;
    setRefetchingImages("all");
    setImageBatchResults(null);
    const token = await getToken();
    if (!token) {
      setRefetchingImages(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "re_fetch_images",
      ids,
      delayMs,
      { mode: "replace" }
    );
    setRefetchingImages(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setImageBatchResults(results);
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    setMissingImages((prev) => {
      const base = prev ?? ids.map((id) => ({ _id: id, name: id }));
      return base.filter((d) => failedIds.has(d._id));
    });
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Retry: ${successCount}/${results.length} succeeded`,
    });
  }, [imageBatchResults, baseUrl, getToken, snackbar, delayMs]);

  const runDestinationExpert = useCallback(
    async (docId: string) => {
      const token = await getToken();
      if (!token) return;
      setBackfilling(docId);
      try {
        const res = await fetchWithAuth(`${baseUrl}/run-destination-expert`, token, { document_id: docId });
        const data = (await res.json()) as { error?: string; skipped?: boolean };
        if (!res.ok) {
          snackbar.open({ type: "error", message: data.error ?? "Failed" });
          return;
        }
        snackbar.open({
          type: data.skipped ? "info" : "success",
          message: data.skipped
            ? `Skipped ${docId} (content already present)`
            : `Content generated for ${docId}`,
        });
        if (!data.skipped) {
          setIncomplete((prev) => prev?.filter((d) => d._id !== docId) ?? null);
        }
      } catch (err) {
        snackbar.open({
          type: "error",
          message: err instanceof Error ? err.message : "Request failed",
        });
      } finally {
        setBackfilling((p) => (p === docId ? null : p));
      }
    },
    [baseUrl, getToken, snackbar]
  );

  const runFillEmptyContent = useCallback(
    async (docId: string) => {
      const token = await getToken();
      if (!token) return;
      setFillingEmpty(docId);
      try {
        const res = await fetchWithAuth(`${baseUrl}/run-destination-expert`, token, {
          document_id: docId,
          fill_empty: true,
        });
        const data = (await res.json()) as { error?: string; skipped?: boolean };
        if (!res.ok) {
          snackbar.open({ type: "error", message: data.error ?? "Failed" });
          return;
        }
        snackbar.open({
          type: data.skipped ? "info" : "success",
          message: data.skipped
            ? `Nothing empty on ${docId}`
            : `Empty fields filled for ${docId}`,
        });
        if (!data.skipped) {
          setPartialContent((prev) => prev?.filter((d) => d._id !== docId) ?? null);
        }
      } catch (err) {
        snackbar.open({
          type: "error",
          message: err instanceof Error ? err.message : "Request failed",
        });
      } finally {
        setFillingEmpty((p) => (p === docId ? null : p));
      }
    },
    [baseUrl, getToken, snackbar]
  );

  const runAllFillEmpty = useCallback(async () => {
    if (!partialContent?.length) return;
    setFillingEmpty("all");
    setPartialBatchResults(null);
    const token = await getToken();
    if (!token) {
      setFillingEmpty(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "run_destination_expert",
      partialContent.map((d) => d._id),
      delayMs,
      { fill_empty: true }
    );
    setFillingEmpty(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setPartialBatchResults(results);
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    setPartialContent((prev) => prev?.filter((d) => failedIds.has(d._id)) ?? null);
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Fill empty: ${successCount}/${results.length} succeeded`,
    });
  }, [partialContent, baseUrl, getToken, snackbar, delayMs]);

  const runAllAppendImages = useCallback(async () => {
    if (!withImages?.length) return;
    setAppendingImages("all");
    setAppendImageBatchResults(null);
    const token = await getToken();
    if (!token) {
      setAppendingImages(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "re_fetch_images",
      withImages.map((d) => d._id),
      delayMs,
      { mode: "append" }
    );
    setAppendingImages(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setAppendImageBatchResults(results);
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Fetch additional: ${successCount}/${results.length} succeeded`,
    });
  }, [withImages, baseUrl, getToken, snackbar, delayMs]);

  const runAllBackfill = useCallback(async () => {
    if (!incomplete?.length) return;
    setBackfilling("all");
    setBackfillBatchResults(null);
    const token = await getToken();
    if (!token) {
      setBackfilling(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "run_destination_expert",
      incomplete.map((d) => d._id),
      delayMs,
    );
    setBackfilling(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setBackfillBatchResults(results);
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    setIncomplete((prev) => prev?.filter((d) => failedIds.has(d._id)) ?? null);
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Backfill: ${successCount}/${results.length} succeeded`,
    });
  }, [incomplete, baseUrl, getToken, snackbar, delayMs]);

  const retryFailedBackfill = useCallback(async () => {
    if (!backfillBatchResults?.length) return;
    const ids = backfillBatchResults.filter((r) => !r.ok).map((r) => r.id);
    if (!ids.length) return;
    setBackfilling("all");
    setBackfillBatchResults(null);
    const token = await getToken();
    if (!token) {
      setBackfilling(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "run_destination_expert",
      ids,
      delayMs,
    );
    setBackfilling(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setBackfillBatchResults(results);
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    setIncomplete((prev) => {
      const base = prev ?? ids.map((id) => ({ _id: id, name: id }));
      return base.filter((d) => failedIds.has(d._id));
    });
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Retry: ${successCount}/${results.length} succeeded`,
    });
  }, [backfillBatchResults, baseUrl, getToken, snackbar, delayMs]);

  const loadAdminDivisions = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingAdminDivisions(true);
    setAdminBatchResults(null);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/destinations/admin-divisions`, token, {});
      const data = (await res.json()) as { destinations?: AdminDivisionListItem[]; error?: string };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed to fetch" });
        return;
      }
      setAdminDivisions(data.destinations ?? []);
      snackbar.open({ type: "success", message: `Found ${(data.destinations ?? []).length} administrative divisions` });
    } catch (err) {
      snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Request failed" });
    } finally {
      setLoadingAdminDivisions(false);
    }
  }, [baseUrl, getToken, snackbar]);

  const runRegenerateContent = useCallback(
    async (docId: string, refetchImages = false) => {
      const token = await getToken();
      if (!token) return;
      setRegeneratingContent(docId);
      try {
        const res = await fetchWithAuth(`${baseUrl}/run-destination-expert`, token, {
          document_id: docId,
          force: true,
          refetch_images: refetchImages,
        });
        const data = (await res.json()) as { error?: string };
        if (!res.ok) {
          snackbar.open({ type: "error", message: data.error ?? "Failed" });
          return;
        }
        snackbar.open({
          type: "success",
          message: refetchImages
            ? `Content + images regenerated for ${docId}`
            : `English text regenerated for ${docId} (images kept)`,
        });
      } catch (err) {
        snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Request failed" });
      } finally {
        setRegeneratingContent((p) => (p === docId ? null : p));
      }
    },
    [baseUrl, getToken, snackbar]
  );

  const runAllRegenerateContent = useCallback(async (refetchImages = false) => {
    if (!adminDivisions?.length) return;
    setRegeneratingContent("all");
    setAdminBatchResults(null);
    const token = await getToken();
    if (!token) {
      setRegeneratingContent(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "run_destination_expert",
      adminDivisions.map((d) => d._id),
      delayMs,
      { force: true, refetch_images: refetchImages }
    );
    setRegeneratingContent(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setAdminBatchResults(results);
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    setAdminDivisions((prev) => prev?.filter((d) => failedIds.has(d._id)) ?? null);
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Regenerate: ${successCount}/${results.length} succeeded`,
    });
  }, [adminDivisions, baseUrl, getToken, snackbar, delayMs]);

  const retryFailedAdmin = useCallback(async () => {
    if (!adminBatchResults?.length) return;
    const ids = adminBatchResults.filter((r) => !r.ok).map((r) => r.id);
    if (!ids.length) return;
    setRegeneratingContent("all");
    setAdminBatchResults(null);
    const token = await getToken();
    if (!token) {
      setRegeneratingContent(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "run_destination_expert",
      ids,
      delayMs,
      { force: true, refetch_images: false }
    );
    setRegeneratingContent(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setAdminBatchResults(results);
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    setAdminDivisions((prev) => {
      const base = prev ?? ids.map((id) => ({ _id: id, name: id }));
      return base.filter((d) => failedIds.has(d._id));
    });
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Retry: ${successCount}/${results.length} succeeded`,
    });
  }, [adminBatchResults, baseUrl, getToken, snackbar, delayMs]);

  const loadWithContent = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingWithContent(true);
    setTranslateBatchResults(null);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/destinations/with-content`, token, listFilterPayload() ?? {});
      const data = (await res.json()) as { destinations?: DocListItem[]; error?: string };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed to fetch" });
        return;
      }
      setWithContent(data.destinations ?? []);
      snackbar.open({ type: "success", message: `Found ${(data.destinations ?? []).length} destinations with content` });
    } catch (err) {
      snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Request failed" });
    } finally {
      setLoadingWithContent(false);
    }
  }, [baseUrl, getToken, snackbar, listFilterPayload]);

  const runTranslate = useCallback(
    async (docId: string, mode: "force" | "fill_empty" | "stale_only" = "force") => {
      const token = await getToken();
      if (!token) return;
      setRegeneratingTranslations(docId);
      try {
        const res = await fetchWithAuth(`${baseUrl}/translate`, token, {
          document_id: docId,
          mode,
        });
        const data = (await res.json()) as {
          ok?: boolean;
          message?: string;
          error?: string;
          skipped?: boolean;
        };
        if (!res.ok) {
          snackbar.open({ type: "error", message: data.error ?? "Failed" });
          return;
        }
        snackbar.open({
          type: data.skipped ? "info" : "success",
          message: data.message ?? "Translation complete",
        });
      } catch (err) {
        snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Request failed" });
      } finally {
        setRegeneratingTranslations((p) => (p === docId ? null : p));
      }
    },
    [baseUrl, getToken, snackbar]
  );

  const runAllRegenerateTranslations = useCallback(async (
    mode: "force" | "fill_empty" | "stale_only" = "force",
  ) => {
    if (!withContent?.length) return;
    setRegeneratingTranslations("all");
    setTranslateBatchResults(null);
    const token = await getToken();
    if (!token) {
      setRegeneratingTranslations(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "translate_destination",
      withContent.map((d) => d._id),
      delayMs,
      { translate_mode: mode }
    );
    setRegeneratingTranslations(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setTranslateBatchResults(results);
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Translate (${mode}): ${successCount}/${results.length} succeeded`,
    });
  }, [withContent, baseUrl, getToken, snackbar, delayMs]);

  const retryFailedTranslate = useCallback(async () => {
    if (!translateBatchResults?.length) return;
    const ids = translateBatchResults.filter((r) => !r.ok).map((r) => r.id);
    if (!ids.length) return;
    setRegeneratingTranslations("all");
    setTranslateBatchResults(null);
    const token = await getToken();
    if (!token) {
      setRegeneratingTranslations(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "translate_destination",
      ids,
      delayMs,
      { translate_mode: "force" }
    );
    setRegeneratingTranslations(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setTranslateBatchResults(results);
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Retry: ${successCount}/${results.length} succeeded`,
    });
  }, [translateBatchResults, baseUrl, getToken, snackbar, delayMs]);

  const handleFixHierarchy = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setFixingHierarchy(true);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/destinations/fix-hierarchy`, token, {});
      const data = (await res.json()) as {
        continents?: number;
        countries?: number;
        administrative_divisions?: number;
        admin_divisions_country_code_set?: number;
        countries_population_set?: number;
        admin_divisions_population_set?: number;
        admin_divisions_geoname_id_set?: number;
        city_names_refreshed?: number;
        live_demoted_to_draft?: number;
        errors?: string[];
        error?: string;
      };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed" });
        return;
      }
      const parts = [
        `${data.continents ?? 0} continents`,
        `${data.countries ?? 0} countries`,
        `${data.administrative_divisions ?? 0} admin divisions`,
      ];
      if ((data.admin_divisions_country_code_set ?? 0) > 0) {
        parts.push(`${data.admin_divisions_country_code_set} country_code set`);
      }
      if ((data.countries_population_set ?? 0) > 0) {
        parts.push(`${data.countries_population_set} country pop.`);
      }
      if ((data.admin_divisions_population_set ?? 0) > 0) {
        parts.push(`${data.admin_divisions_population_set} admin pop.`);
      }
      if ((data.admin_divisions_geoname_id_set ?? 0) > 0) {
        parts.push(`${data.admin_divisions_geoname_id_set} admin geoname_id set`);
      }
      if ((data.city_names_refreshed ?? 0) > 0) {
        parts.push(`${data.city_names_refreshed} city name_localized refreshed`);
      }
      const demoted = data.live_demoted_to_draft ?? 0;
      const publishHint =
        demoted > 0
          ? ` · ${demoted} moved to Live · Draft — mark Live · Ready, then Publish to update the site`
          : " · Mark affected docs Live · Ready and Publish to update the site";
      snackbar.open({
        type: "success",
        message: `Hierarchy fixed: ${parts.join(", ")}${publishHint}`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setFixingHierarchy(false);
    }
  }, [baseUrl, getToken, snackbar]);

  const handleCleanupFalseMidtiers = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setCleaningMidtiers(true);
    try {
      const res = await fetchWithAuth(
        `${baseUrl}/api/destinations/cleanup-false-midtiers`,
        token,
        {},
      );
      const data = (await res.json()) as {
        reparented?: number;
        deleted?: number;
        renamed?: number;
        errors?: string[];
        error?: string;
      };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed" });
        return;
      }
      const errHint =
        data.errors && data.errors.length > 0
          ? ` · ${data.errors.length} warning(s): ${data.errors.slice(0, 2).join("; ")}`
          : "";
      snackbar.open({
        type: data.errors?.length ? "warning" : "success",
        message: `False mid-tiers cleanup: ${data.deleted ?? 0} deleted, ${data.reparented ?? 0} reparented, ${data.renamed ?? 0} renamed${errHint}. Publish canonical Balears / renamed regions if still Draft.`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setCleaningMidtiers(false);
    }
  }, [baseUrl, getToken, snackbar]);

  const handleDedupeAdminDivisionTypes = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setDedupingTypes(true);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/admin-division-types/dedupe`, token, {});
      const data = (await res.json()) as {
        merged_groups?: number;
        deleted?: number;
        repointed_destinations?: number;
        errors?: string[];
        error?: string;
      };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed" });
        return;
      }
      const errHint =
        data.errors && data.errors.length > 0
          ? ` · ${data.errors.length} warning(s): ${data.errors.slice(0, 2).join("; ")}`
          : "";
      snackbar.open({
        type: data.errors?.length ? "warning" : "success",
        message: `Type dedupe: ${data.merged_groups ?? 0} groups merged, ${data.deleted ?? 0} deleted, ${data.repointed_destinations ?? 0} destinations repointed${errHint}`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setDedupingTypes(false);
    }
  }, [baseUrl, getToken, snackbar]);

  const handleBackfillAdminTier = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setBackfillingTier(true);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/destinations/backfill-admin-tier`, token, {});
      const data = (await res.json()) as {
        updated?: number;
        skipped?: number;
        errors?: string[];
        error?: string;
      };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed" });
        return;
      }
      const errHint =
        data.errors && data.errors.length > 0
          ? ` · ${data.errors.length} warning(s): ${data.errors.slice(0, 2).join("; ")}`
          : "";
      snackbar.open({
        type: data.errors?.length ? "warning" : "success",
        message: `Admin tier backfill: ${data.updated ?? 0} updated, ${data.skipped ?? 0} unchanged${errHint}`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setBackfillingTier(false);
    }
  }, [baseUrl, getToken, snackbar]);

  const handleFixParent = useCallback(async () => {
    if (!countryCode || countryCode.length !== 2) {
      snackbar.open({
        type: "error",
        message: "Country code must be a 2-letter ISO code (e.g. FR, DE)",
      });
      return;
    }
    const token = await getToken();
    if (!token) return;
    setFixingParent(true);
    try {
      const res = await fetchWithAuth(`${baseUrl}/seed`, token, {
        country_code: countryCode.toUpperCase(),
        population_threshold: populationThreshold,
        parent_id: parentId || null,
        update_existing: true,
      });
      const data = (await res.json()) as {
        created?: number;
        skipped?: number;
        updated?: number;
        tourist_regions?: number;
        tourist_regions_existing?: number;
        islands?: number;
        islands_existing?: number;
        live_demoted_to_draft?: number;
        errors?: string[];
        error?: string;
      };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed" });
        return;
      }
      const demoted = data.live_demoted_to_draft ?? 0;
      const parts = [
        `${data.updated ?? 0} updated`,
        `${data.created ?? 0} created`,
        `${data.skipped ?? 0} skipped`,
      ];
      if ((data.tourist_regions ?? 0) + (data.tourist_regions_existing ?? 0) > 0) {
        parts.push(
          `tourist regions ${data.tourist_regions ?? 0} new / ${data.tourist_regions_existing ?? 0} existing`,
        );
      }
      if ((data.islands ?? 0) + (data.islands_existing ?? 0) > 0) {
        parts.push(`islands ${data.islands ?? 0} new / ${data.islands_existing ?? 0} existing`);
      }
      const publishHint =
        demoted > 0
          ? ` · ${demoted} moved to Live · Draft — mark Live · Ready, then Publish`
          : "";
      const errHint =
        data.errors && data.errors.length > 0
          ? ` · ${data.errors.length} warning(s)`
          : "";
      snackbar.open({
        type: "success",
        message: `Seed complete: ${parts.join(", ")}${publishHint}${errHint}`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setFixingParent(false);
    }
  }, [baseUrl, countryCode, populationThreshold, parentId, getToken, snackbar]);

  return (
    <div className="space-y-6">
      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          List filters (optional)
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Narrow destination lists by ISO country code and/or level. Leave blank to include all.
        </Typography>
        <div className="flex flex-wrap gap-4 items-end max-w-xl">
          <TextField
            label="Country code (ISO2)"
            value={listFilterCountry}
            onChange={(e) => setListFilterCountry(e.target.value.toUpperCase().slice(0, 2))}
            placeholder="e.g. CH"
          />
          <div className="flex flex-col gap-1">
            <Typography variant="caption" className="text-gray-600">
              Level
            </Typography>
            <select
              className="border rounded px-2 py-2 min-w-[12rem]"
              value={listFilterLevel}
              onChange={(e) => setListFilterLevel(e.target.value)}
              aria-label="Destination level filter"
            >
              {DESTINATION_LEVELS.map((opt) => (
                <option key={opt.value || "any"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Backfill content
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Destinations without content.en. Run one destination, or Run all empty destinations.
          Pipeline: research brief (draft→challenge→revise) → body → At a glance → titles → meta → images.
        </Typography>
        <Button variant="outlined" onClick={loadIncomplete} disabled={loadingIncomplete}>
          {loadingIncomplete ? "Loading…" : "List destinations without content"}
        </Button>
        {incomplete !== null && (
          <div className="mt-4">
            {incomplete.length === 0 ? (
              <Typography variant="body2" className="text-green-600">
                All destinations have content.
              </Typography>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Typography variant="body2">
                    {incomplete.length} destination(s) need content:
                  </Typography>
                  <Button
                    variant="filled"
                    size="small"
                    color="primary"
                    onClick={runAllBackfill}
                    disabled={!!backfilling}
                  >
                    {backfilling === "all" ? "Running…" : "Run all"}
                  </Button>
                </div>
                <ul className="list-disc list-inside space-y-1 max-h-48 overflow-y-auto">
                  {incomplete.map((d) => (
                    <li key={d._id} className="flex items-center gap-2">
                      <span>{d.name || d._id}</span>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => runDestinationExpert(d._id)}
                        disabled={!!backfilling}
                      >
                        {backfilling === d._id ? "Running…" : "Run"}
                      </Button>
                    </li>
                  ))}
                </ul>
                <BatchResultBar
                  label="Last batch"
                  results={backfillBatchResults}
                  onRetryFailed={retryFailedBackfill}
                  retryDisabled={!!backfilling}
                />
              </>
            )}
          </div>
        )}
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Fill empty content fields
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Destinations that already have some content but are missing headline, intro, stats, FAQs,
          At a glance, SEO fields, research brief, or keywords.
          Fills only empty fields — existing text is kept. Use Run for one destination, or Fill all empty.
        </Typography>
        <Button variant="outlined" onClick={loadPartialContent} disabled={loadingPartialContent}>
          {loadingPartialContent ? "Loading…" : "List destinations with empty fields"}
        </Button>
        {partialContent !== null && (
          <div className="mt-4">
            {partialContent.length === 0 ? (
              <Typography variant="body2" className="text-green-600">
                No destinations with empty content fields.
              </Typography>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Typography variant="body2">
                    {partialContent.length} destination(s) with empty fields:
                  </Typography>
                  <Button
                    variant="filled"
                    size="small"
                    color="primary"
                    onClick={runAllFillEmpty}
                    disabled={!!fillingEmpty}
                  >
                    {fillingEmpty === "all" ? "Filling…" : "Fill all empty"}
                  </Button>
                </div>
                <ul className="list-disc list-inside space-y-1 max-h-48 overflow-y-auto">
                  {partialContent.map((d) => (
                    <li key={d._id} className="flex items-center gap-2">
                      <span>{d.name || d._id}</span>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => runFillEmptyContent(d._id)}
                        disabled={!!fillingEmpty}
                      >
                        {fillingEmpty === d._id ? "Filling…" : "Fill empty"}
                      </Button>
                    </li>
                  ))}
                </ul>
                <BatchResultBar
                  label="Last batch"
                  results={partialBatchResults}
                  onRetryFailed={async () => {
                    if (!partialBatchResults?.length) return;
                    const ids = partialBatchResults.filter((r) => !r.ok).map((r) => r.id);
                    if (!ids.length) return;
                    setFillingEmpty("all");
                    setPartialBatchResults(null);
                    const token = await getToken();
                    if (!token) {
                      setFillingEmpty(null);
                      return;
                    }
                    const { ok, results, error } = await runServerBatch(
                      baseUrl,
                      token,
                      "run_destination_expert",
                      ids,
                      delayMs,
                      { fill_empty: true }
                    );
                    setFillingEmpty(null);
                    if (!ok) {
                      snackbar.open({ type: "error", message: error ?? "Batch failed" });
                      return;
                    }
                    setPartialBatchResults(results);
                  }}
                  retryDisabled={!!fillingEmpty}
                />
              </>
            )}
          </div>
        )}
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Regenerate content (administrative divisions)
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Regenerate English text for cantons, states, and regions. Overwrites existing copy with
          region-focused text. Default keeps keywords and images; use + images to also refresh the gallery.
        </Typography>
        <Button variant="outlined" onClick={loadAdminDivisions} disabled={loadingAdminDivisions}>
          {loadingAdminDivisions ? "Loading…" : "List administrative divisions"}
        </Button>
        {adminDivisions !== null && (
          <div className="mt-4">
            {adminDivisions.length === 0 ? (
              <Typography variant="body2" className="text-green-600">
                No administrative divisions found.
              </Typography>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Typography variant="body2">{adminDivisions.length} administrative division(s):</Typography>
                  <Button
                    variant="filled"
                    size="small"
                    color="primary"
                    onClick={() => runAllRegenerateContent(false)}
                    disabled={!!regeneratingContent}
                  >
                    {regeneratingContent === "all" ? "Regenerating…" : "Regenerate all (text)"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => runAllRegenerateContent(true)}
                    disabled={!!regeneratingContent}
                  >
                    Regenerate all (text + images)
                  </Button>
                </div>
                <ul className="list-disc list-inside space-y-1 max-h-64 overflow-y-auto">
                  {adminDivisions.map((d) => (
                    <li key={d._id} className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                      <span className="font-medium">{d.name || d._id}</span>
                      <span className="text-sm text-gray-600">
                        tier={d.admin_tier || "—"} · type={d.type_code || "—"} · feature={d.feature_code || "—"} · admin={d.admin_code || "—"}
                        {d.parent_name ? ` · parent=${d.parent_name}` : d.parent_id ? ` · parent_id=${d.parent_id}` : ""}
                      </span>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => runRegenerateContent(d._id, false)}
                        disabled={!!regeneratingContent}
                      >
                        {regeneratingContent === d._id ? "…" : "Text"}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => runRegenerateContent(d._id, true)}
                        disabled={!!regeneratingContent}
                      >
                        Text + images
                      </Button>
                    </li>
                  ))}
                </ul>
                <BatchResultBar
                  label="Last batch"
                  results={adminBatchResults}
                  onRetryFailed={retryFailedAdmin}
                  retryDisabled={!!regeneratingContent}
                />
              </>
            )}
          </div>
        )}
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Translations
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Translate all overwrites DE/FR/IT. Fill untranslated only fills empty fields. Re-translate stale updates fields where English changed since the last translation. Field-by-field actions are on each destination’s Localization Review tab.
        </Typography>
        <Button variant="outlined" onClick={loadWithContent} disabled={loadingWithContent}>
          {loadingWithContent ? "Loading…" : "List destinations with content"}
        </Button>
        {withContent !== null && (
          <div className="mt-4">
            {withContent.length === 0 ? (
              <Typography variant="body2" className="text-green-600">
                No destinations have content.
              </Typography>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Typography variant="body2">{withContent.length} destination(s) with content:</Typography>
                  <Button
                    variant="filled"
                    size="small"
                    color="primary"
                    onClick={() => void runAllRegenerateTranslations("force")}
                    disabled={!!regeneratingTranslations}
                  >
                    {regeneratingTranslations === "all" ? "Translating…" : "Translate all"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => void runAllRegenerateTranslations("fill_empty")}
                    disabled={!!regeneratingTranslations}
                  >
                    Fill untranslated
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => void runAllRegenerateTranslations("stale_only")}
                    disabled={!!regeneratingTranslations}
                  >
                    Re-translate stale
                  </Button>
                </div>
                <ul className="list-disc list-inside space-y-1 max-h-48 overflow-y-auto">
                  {withContent.map((d) => (
                    <li key={d._id} className="flex items-center gap-2 flex-wrap">
                      <span>{d.name || d._id}</span>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => void runTranslate(d._id, "force")}
                        disabled={!!regeneratingTranslations}
                      >
                        {regeneratingTranslations === d._id ? "…" : "All"}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => void runTranslate(d._id, "fill_empty")}
                        disabled={!!regeneratingTranslations}
                      >
                        Fill empty
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => void runTranslate(d._id, "stale_only")}
                        disabled={!!regeneratingTranslations}
                      >
                        Stale
                      </Button>
                    </li>
                  ))}
                </ul>
                <BatchResultBar
                  label="Last batch"
                  results={translateBatchResults}
                  onRetryFailed={retryFailedTranslate}
                  retryDisabled={!!regeneratingTranslations}
                />
              </>
            )}
          </div>
        )}
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Images
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Re-fetch all replaces existing images. Fetch additional appends new Unsplash results without removing current ones.
        </Typography>

        <Typography variant="subtitle2" className="font-medium mb-2">
          Re-fetch all (missing images)
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Destinations that have keywords but no images. Replaces/sets the full image set.
        </Typography>
        <Button variant="outlined" onClick={loadMissingImages} disabled={loadingMissingImages}>
          {loadingMissingImages ? "Loading…" : "List destinations missing images"}
        </Button>
        {missingImages !== null && (
          <div className="mt-4 mb-6">
            {missingImages.length === 0 ? (
              <Typography variant="body2" className="text-green-600">
                All destinations with keywords have images.
              </Typography>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Typography variant="body2">{missingImages.length} destination(s) need images:</Typography>
                  <Button
                    variant="filled"
                    size="small"
                    color="primary"
                    onClick={runAllReFetchImages}
                    disabled={!!refetchingImages}
                  >
                    {refetchingImages === "all" ? "Re-fetching…" : "Re-fetch all"}
                  </Button>
                </div>
                <ul className="list-disc list-inside space-y-1 max-h-48 overflow-y-auto">
                  {missingImages.map((d) => (
                    <li key={d._id} className="flex items-center gap-2">
                      <span>{d.name || d._id}</span>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => runReFetchImages(d._id)}
                        disabled={!!refetchingImages}
                      >
                        {refetchingImages === d._id ? "Re-fetching…" : "Re-fetch all"}
                      </Button>
                    </li>
                  ))}
                </ul>
                <BatchResultBar
                  label="Last batch"
                  results={imageBatchResults}
                  onRetryFailed={retryFailedImages}
                  retryDisabled={!!refetchingImages}
                />
              </>
            )}
          </div>
        )}

        <Typography variant="subtitle2" className="font-medium mb-2">
          Fetch additional (keep existing)
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Destinations that already have images. Appends new photos; existing images are kept.
        </Typography>
        <Button variant="outlined" onClick={loadWithImages} disabled={loadingWithImages}>
          {loadingWithImages ? "Loading…" : "List destinations with images"}
        </Button>
        {withImages !== null && (
          <div className="mt-4">
            {withImages.length === 0 ? (
              <Typography variant="body2" className="text-green-600">
                No destinations with images found.
              </Typography>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Typography variant="body2">{withImages.length} destination(s) with images:</Typography>
                  <Button
                    variant="filled"
                    size="small"
                    color="primary"
                    onClick={runAllAppendImages}
                    disabled={!!appendingImages}
                  >
                    {appendingImages === "all" ? "Fetching…" : "Fetch additional all"}
                  </Button>
                </div>
                <ul className="list-disc list-inside space-y-1 max-h-48 overflow-y-auto">
                  {withImages.map((d) => (
                    <li key={d._id} className="flex items-center gap-2">
                      <span>
                        {d.name || d._id}
                        {typeof d.image_count === "number" ? ` (${d.image_count})` : ""}
                      </span>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => runAppendImages(d._id)}
                        disabled={!!appendingImages}
                      >
                        {appendingImages === d._id ? "Fetching…" : "Fetch additional"}
                      </Button>
                    </li>
                  ))}
                </ul>
                <BatchResultBar
                  label="Last batch"
                  results={appendImageBatchResults}
                  onRetryFailed={async () => {
                    if (!appendImageBatchResults?.length) return;
                    const ids = appendImageBatchResults.filter((r) => !r.ok).map((r) => r.id);
                    if (!ids.length) return;
                    setAppendingImages("all");
                    setAppendImageBatchResults(null);
                    const token = await getToken();
                    if (!token) {
                      setAppendingImages(null);
                      return;
                    }
                    const { ok, results, error } = await runServerBatch(
                      baseUrl,
                      token,
                      "re_fetch_images",
                      ids,
                      delayMs,
                      { mode: "append" }
                    );
                    setAppendingImages(null);
                    if (!ok) {
                      snackbar.open({ type: "error", message: error ?? "Batch failed" });
                      return;
                    }
                    setAppendImageBatchResults(results);
                  }}
                  retryDisabled={!!appendingImages}
                />
              </>
            )}
          </div>
        )}
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Duplicates
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Destinations with the same name, country_code, and level. Prefer the one with geoname_id (from seed). Delete the manual duplicate in the Destinations collection.
        </Typography>
        <Button variant="outlined" onClick={loadDuplicates} disabled={loadingDuplicates}>
          {loadingDuplicates ? "Loading…" : "List duplicates"}
        </Button>
        {duplicates !== null && (
          <div className="mt-4">
            {duplicates.length === 0 ? (
              <Typography variant="body2" className="text-green-600">
                No duplicates found.
              </Typography>
            ) : (
              <ul className="space-y-3 list-none">
                {duplicates.map((group, i) => (
                  <li key={i} className="border rounded p-3 bg-gray-50">
                    <Typography variant="body2" className="font-medium mb-2">
                      {group[0]?.name ?? "Unknown"} ({group.length} copies)
                    </Typography>
                    {group.map((d) => (
                      <div key={d._id} className="text-sm text-gray-600">
                        {d._id} — geoname_id: {d.geoname_id}
                        {d.geoname_id > 0 ? " (from seed)" : " (manual)"}
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Fix hierarchy (all levels)
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Correct parent_id and set population from GeoNames for continents, countries, and
          administrative divisions. Live destinations are moved to Live · Draft — mark Live · Ready,
          then use Publish in the top bar to rebuild the site.
        </Typography>
        <Button variant="filled" color="primary" onClick={handleFixHierarchy} disabled={fixingHierarchy}>
          {fixingHierarchy ? "Fixing…" : "Fix hierarchy"}
        </Button>
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Cleanup false mid-tiers (hard delete)
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Removes bad ADM2-as-island docs (e.g. duplicate Balearic Islands, Madrid island), reparents
          children to the canonical ADM1, and renames Spanish uniprovincial regions (Comunidad de
          Madrid, Región de Murcia, …). Hard-deletes losers — intended for test/dev data.
        </Typography>
        <Button
          variant="filled"
          color="primary"
          onClick={handleCleanupFalseMidtiers}
          disabled={cleaningMidtiers}
        >
          {cleaningMidtiers ? "Cleaning…" : "Cleanup false mid-tiers"}
        </Button>
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Admin division types dedupe
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Merges duplicate admin_division_types rows (e.g. multiple Island types from race conditions).
          Keeps one survivor per code, repoints destination type_id references, and hard-deletes orphans.
        </Typography>
        <Button
          variant="filled"
          color="primary"
          onClick={handleDedupeAdminDivisionTypes}
          disabled={dedupingTypes}
        >
          {dedupingTypes ? "Deduping…" : "Dedupe admin division types"}
        </Button>
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Backfill admin tier
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Recomputes admin_tier (Primary / Nested / Tourist) on all administrative divisions from parent_id
          and type_code. Run after cleanup or fix-hierarchy if CMS tier columns look empty.
        </Typography>
        <Button
          variant="filled"
          color="primary"
          onClick={handleBackfillAdminTier}
          disabled={backfillingTier}
        >
          {backfillingTier ? "Backfilling…" : "Backfill admin tier"}
        </Button>
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Fix parent_id (cities)
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Re-run seed with update_existing to correct parent_id for cities matched by geoname_id.
          Also ensures curated tourist regions (e.g. Costa Brava) and islands (e.g. Mallorca) as{" "}
          Draft — independent of the population threshold. Live cities are moved to Live · Draft —
          mark Live · Ready, then Publish. Use a lower threshold (e.g. 100000) to include Palma and
          other mid-size cities under those islands.
        </Typography>
        <div className="flex flex-col gap-4 max-w-sm">
          <TextField
            label="Country Code (ISO 3166 alpha2)"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            placeholder="e.g. CH, FR"
          />
          <TextField
            label="Population Threshold"
            type="number"
            value={String(populationThreshold)}
            onChange={(e) => setPopulationThreshold(parseInt(e.target.value, 10) || 500_000)}
          />
          <TextField
            label="Parent ID (optional)"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            placeholder="Leave empty to auto-create continent/country"
          />
          <Button variant="filled" color="primary" onClick={handleFixParent} disabled={fixingParent || !countryCode}>
            {fixingParent ? "Updating…" : "Seed with update_existing"}
          </Button>
        </div>
      </Paper>
    </div>
  );
}
